import React, { useState, useEffect, useRef } from 'react'
import { Form } from 'react-bootstrap'
import Card from '../Card'
import { apiHandler } from '../../api/apihandler';
import { UPDATE_PHOTOGRAPH_USE, PROFILE_DETAILS } from "../../api/constants";
import Swal from 'sweetalert2'

// Redux Selector / Action
import { useDispatch, useSelector } from 'react-redux';
import { setUserData } from '../../store/setting/actions'

const PictureApproval = () => {
    const dispatch = useDispatch()
    let profileData = useSelector((state) => state.setting.setting.user_data)
    profileData = profileData ? JSON.parse(profileData) : null;

    const [photographUse, setPhotographUse] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isUpdatingRef = useRef(false);

    // Initialize toggle state from Redux store (profile/details is called on page load)
    useEffect(() => {
        if (!isUpdatingRef.current && profileData?.user_setting?.is_student_photograph_use !== undefined) {
            // Convert database value (0 or 1) to boolean
            const dbValue = profileData.user_setting.is_student_photograph_use;
            setPhotographUse(dbValue === 1);
        }
    }, [profileData?.user_setting?.is_student_photograph_use]);

    const handleToggle = async () => {
        if (isLoading) return;
        
        const currentState = photographUse;
        const newState = !currentState;
        const apiPayload = newState ? "1" : "0"; // true = 1 (ON), false = 0 (OFF)
        
        // Prevent useEffect from interfering during update
        isUpdatingRef.current = true;
        // Optimistically update UI for immediate feedback
        setPhotographUse(newState);
        setIsLoading(true);

        try {
            // Step 1: Call update API
            console.log('📤 Sending payload:', { is_student_photograph_use: apiPayload });
            const updateRes = await apiHandler(UPDATE_PHOTOGRAPH_USE, "POST", {
                is_student_photograph_use: apiPayload
            });
            console.log('📥 Update API response:', updateRes.data);

            if (updateRes.data?.response?.status === 200) {
                // Step 2: Fetch fresh profile data to verify what's stored in database
                const profileRes = await apiHandler(PROFILE_DETAILS, "POST");
                console.log('📥 Profile API response:', profileRes.data?.data?.user?.user_setting?.is_student_photograph_use);
                
                if (profileRes.data?.response?.status === 200) {
                    const actualDbValue = profileRes.data?.data?.user?.user_setting?.is_student_photograph_use;
                    
                    // Update state to match actual database value
                    setPhotographUse(actualDbValue === 1);
                    
                    // Update Redux store with complete fresh profile data
                    const userData = profileRes.data?.data?.user;
                    if (userData) {
                        dispatch(setUserData(JSON.stringify(userData)));
                    }
                    
                    // Show success message
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `Picture approval ${actualDbValue === 1 ? 'enabled' : 'disabled'} successfully.`,
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    // Couldn't verify - revert to previous state
                    setPhotographUse(currentState);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to verify the update.',
                    });
                }
            } else {
                // Update API failed - revert to previous state
                setPhotographUse(currentState);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: updateRes.data?.response?.message || 'Failed to update photograph approval setting.',
                });
            }
        } catch (error) {
            // Error occurred - revert to previous state
            setPhotographUse(currentState);
            console.error('Error updating photograph approval:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while updating the setting.',
            });
        } finally {
            setIsLoading(false);
            // Allow useEffect to sync after a brief delay
            setTimeout(() => {
                isUpdatingRef.current = false;
            }, 150);
        }
    };

    // Show only for students (normal users)
    // Hide for: Admin (user_type=4), Teacher (user_type=2), Influencers, Dance corner users
    // And only when CLASS_AVAILABLE is enabled
    const shouldShow = profileData &&
        (process.env.REACT_APP_IS_CLASS_AVAILABLE === 1 || process.env.REACT_APP_IS_CLASS_AVAILABLE === "1") &&
        (profileData?.user_setting?.is_influencer === 0 && profileData?.user_setting?.is_dance_corner === 0) &&
        profileData?.user_type !== 4 &&
        profileData?.user_type !== 2;

    if (!shouldShow) {
        return null;
    }

    return (
        <Card>
            <Card.Body>
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">Picture Approval</h5>
                        <Form.Check
                            type="switch"
                            id="picture-approval-switch"
                            checked={photographUse}
                            onChange={handleToggle}
                            disabled={isLoading}
                            style={{ transform: 'scale(1.3)' }}
                        />
                    </div>
                    <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                        I give permission for my child's photograph to be taken and shared on the school's official social media platforms.
                    </p>
                </div>
            </Card.Body>
        </Card>
    );
};

export default PictureApproval;
